import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { doPOST } from '../utils/HttpUtils';
import { useAppContext } from '../services/context/AppContext';
import { hasRole } from '../utils/helper';
import { MODULES } from '../utils/constants';

export default function UtilisationEmailControls({ source, filters, disabled = false }: {
    source: 'internal' | 'external' | 'events';
    filters: Record<string, any>;
    disabled?: boolean;
}) {
    const { userData } = useAppContext();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<{ error: boolean; message: string } | null>(null);
    const isHR = hasRole(userData?.role, MODULES.HR) || hasRole(userData?.backendRole, MODULES.HR);
    const allowed = isHR || hasRole(userData?.role, MODULES.ADMIN) || hasRole(userData?.backendRole, MODULES.ADMIN);
    if (!allowed) return null;
    const send = async () => {
        if (sending || disabled || !email.trim()) return;
        setSending(true);
        setResult(null);
        try {
            const applied = Object.fromEntries(Object.entries(filters).filter(([key, value]) =>
                !['page', 'limit'].includes(key) && value !== '' && value !== undefined && value !== null));
            const response = await doPOST('/utilisation-reports', {
                source, filters: applied, recipient: 'email', email: email.trim(),
            });
            if (response?.error || response?.status !== 200 || !response?.data?.success) {
                throw new Error(response?.message || 'Unable to send utilisation report');
            }
            setResult({ error: false, message: `Report accepted for delivery to ${response.data.data.recipients.join(', ')}. Matching bookings: ${response.data.data.metrics.totalBookings}.` });
        } catch (error: any) {
            setResult({ error: true, message: error.message || 'Unable to send utilisation report' });
        } finally { setSending(false); }
    };
    return <>
        <Box sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<EmailOutlinedIcon />} disabled={disabled}
                onClick={() => { setResult(null); setOpen(true); }}>Email</Button>
        </Box>
        <Dialog open={open} onClose={() => { if (!sending) setOpen(false); }} fullWidth maxWidth="sm"
            aria-labelledby="utilisation-email-title">
            <Box component="form" onSubmit={(event: React.FormEvent) => { event.preventDefault(); void send(); }}>
                <DialogTitle id="utilisation-email-title">Email utilisation report</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                        <Box>
                            <Chip size="small" variant="outlined" sx={{ mb: 1 }}
                                label={source === 'events' ? 'Events' : `${source === 'internal' ? 'Internal' : 'External'} bookings`} />
                            <Typography variant="body2" color="text.secondary">
                                Includes every record matching your current filters, across all pages.
                            </Typography>
                        </Box>
                        <TextField label="Recipient email" placeholder="name@company.com" type="email" required
                            autoFocus value={email} onChange={event => setEmail(event.target.value)} disabled={sending} fullWidth />
                        {result && <Alert severity={result.error ? 'error' : 'success'}>{result.message}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setOpen(false)} disabled={sending}>{result && !result.error ? 'Close' : 'Cancel'}</Button>
                    <Button type="submit" variant="contained" disableElevation startIcon={<SendOutlinedIcon />}
                        disabled={sending || disabled || !email.trim()}>{sending ? 'Sending…' : 'Send report'}</Button>
                </DialogActions>
            </Box>
        </Dialog>
    </>;
}
